import { use, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserData } from "@/components/user/lib/hooks/useUserData";
import { UseData } from "@/components/user/lib/services/UseData";
import { AuthContext } from "@/context/AuthContext";
import { useGetStripeRoles } from "@/hooks/useGetUserRoles/useGetStripeRoles";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { LocalStorageKeys } from "@/shared/types/common";
import { checkIfCurrentPlan } from "../helpers/checkIfCurrentPlan";
import { handleClickOnCard } from "../helpers/handleClickOnCard";
import { onSuccessUpdate } from "../helpers/onSuccessUpdate";
import { stripeCheckout } from "../helpers/stripeCheckout";
import { updateSubscription } from "../helpers/updateSubscription";
import type {
	BillingAddress,
	FormType,
	PricingCardType,
	StripeUpdateData,
	SubscriptionPeriod,
	SubscriptionPlan,
} from "../types";
import { useReferralId } from "../useReferralId";
import { PricingCardList } from "./component/CardList";
import { pricingList } from "./component/consts";
import { RenderDialogContent } from "./component/RenderDialogContent";

type Props = {
	setSubscriptionWillCancel?: (value: boolean) => void;
	subscriptionWillCancel?: boolean;
	otherProps?: React.HTMLAttributes<HTMLDivElement>;
};

const periods: { label: string; value: SubscriptionPeriod }[] = [
	{ label: "Monthly", value: "month" },
	{ label: "Yearly", value: "year" },
];

const tabTriggerClassName =
	"max-w-fit px-5 py-2 cursor-pointer text-gray data-[state=active]:text-foreground data-[state=active]:[background:linear-gradient(black)_padding-box,linear-gradient(180deg,#C9C9C9_0%,rgba(99,99,99,0)_100%)_border-box] data-[state=active]:hover:[background:linear-gradient(black)_padding-box,linear-gradient(180deg,#C9C9C9_0%,rgba(99,99,99,0.7)_100%)_border-box] hover:text-foreground rounded-xl border-1 border-transparent";

export const PricingTable: React.FC<Props> = ({
	setSubscriptionWillCancel = () => {},
	subscriptionWillCancel = false,
	otherProps,
}) => {
	const [period, setPeriod] = useState<SubscriptionPeriod>("month");
	const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
		null,
	);
	const [openModal, setOpenModal] = useState<FormType | null>(null);
	const stripeRoles = useGetStripeRoles();
	const role = stripeRoles?.length ? stripeRoles[0] : null;
	const { pathname } = useLocation();
	const context = use(AuthContext);
	const { userData } = useUserData();
	const stripeId = userData?.stripeSubscriptionId || null;
	const navigate = useNavigate();

	const tabsContent = useMemo(
		() =>
			periods.map(({ value: period }) => ({
				period,
				list: pricingList
					.map((item) => {
						return {
							...item,
							price: period === "month" ? item.monthlyPrice : item.yearlyPrice,
							period,
							isCurrentPlan: subscriptionWillCancel
								? false
								: checkIfCurrentPlan(period, item, userData, role),
						};
					})
					.filter(Boolean) as PricingCardType[],
			})),
		[subscriptionWillCancel, role, userData, userData?.subscriptionInterval],
	);

	const [, setValue] = useLocalStorage<string | null>(
		LocalStorageKeys.redirectPath,
		null,
	);

	const [, setAttemptToBuy] = useLocalStorage<boolean | null>(
		LocalStorageKeys.attemptToBuy,
		null,
	);

	const { create } = UseData;
	const {
		useGetStripeUpdateSubscription,
		useGetStripeCheckout,
		useDeleteStripeSubscription,
	} = create();
	const { execute: getCheckoutUrl } = useGetStripeCheckout();
	const { execute: updatePlan } = useGetStripeUpdateSubscription();
	const { execute: cancelPlan } = useDeleteStripeSubscription();

	const { referralId, couponId } = useReferralId();

	const successHandler = useCallback(
		(shouldRedirectToDashboard?: boolean) =>
			onSuccessUpdate({
				shouldRedirectToDashboard,
				navigate,
				setOpenModal,
				pathname,
			}),
		[navigate, pathname],
	);

	const checkoutHandler = useCallback(
		async (address: BillingAddress) => {
			await stripeCheckout({
				address,
				selectedPlan,
				period,
				referralId,
				couponId,
				getCheckoutUrl,
				setOpenModal,
			});
		},
		[selectedPlan, period, getCheckoutUrl, referralId, couponId],
	);

	const updateSubscriptionHandler = useCallback(
		(plan: SubscriptionPlan | null, period: SubscriptionPeriod) =>
			updateSubscription({
				plan,
				period,
				updatePlan,
				cancelPlan,
				setOpenModal,
				context,
				userData,
				setSubscriptionWillCancel,
			}),
		[context, updatePlan, setSubscriptionWillCancel, userData, cancelPlan],
	);

	const clickOnCardHandler = useCallback(
		({ plan, period }: StripeUpdateData) =>
			handleClickOnCard({
				cardData: { plan, period },
				navigate,
				setValue,
				context,
				setSelectedPlan,
				setOpenModal,
				stripeId,
				setAttemptToBuy,
			}),
		[navigate, setValue, context, stripeId, setAttemptToBuy],
	);

	return (
		<div
			className="flex flex-col gap-16 items-center w-full @container"
			data-testid="pricing-table"
			{...otherProps}
		>
			<Tabs
				defaultValue="month"
				className="flex flex-col w-fit rounded-none p-0 gap-8"
				onValueChange={(value: string) => {
					setPeriod(value as SubscriptionPeriod);
				}}
				value={period}
			>
				<TabsList className="w-full">
					{periods.map(({ label, value }) => (
						<TabsTrigger
							key={value}
							value={value}
							className={tabTriggerClassName}
						>
							{label}
						</TabsTrigger>
					))}
				</TabsList>

				{tabsContent.map(({ period, list }) => (
					<TabsContent key={period} value={period}>
						<PricingCardList
							list={list}
							onSubscriptionChange={clickOnCardHandler}
						/>
					</TabsContent>
				))}
			</Tabs>

			<p className="text-center font-afacad font-medium text-[16px]">
				All Services include unlimited access to Product Support, Knowledge Lab,
				User Webinars and level specific product enhancements.
			</p>

			{openModal && (
				<RenderDialogContent
					openModal={openModal}
					stripeCheckout={checkoutHandler}
					onSuccessUpdate={successHandler}
					updateSubscription={updateSubscriptionHandler}
					setOpenModal={setOpenModal}
					period={period}
					selectedPlan={selectedPlan}
					userData={userData}
				/>
			)}
		</div>
	);
};
